import { useEffect, useState } from 'react';
import { api, errorMessage } from '../api/client';
import type { Changes, GameConfig } from '../api/types';
import { useResource } from '../api/useResource';
import { Field, NumberInput, Panel, Spinner, Status, Toggle, summariseChanges } from '../components/ui';

type Draft = Omit<GameConfig, 'id' | 'version' | 'updatedAt'>;

const NUMBER_FIELDS: { key: keyof Draft; label: string; hint?: string }[] = [
  { key: 'turnTimerSeconds', label: 'Turn timer (seconds)', hint: 'Spec 2.6 [OPEN]' },
  { key: 'maxCardExchangePerAction', label: 'Max cards per exchange' },
  { key: 'minPlayers', label: 'Minimum players' },
  { key: 'maxPlayers', label: 'Maximum players' },
  { key: 'totalOrgansToCure', label: 'Organs to cure to win' },
  {
    key: 'boardSpaces',
    label: 'Board spaces',
    hint: 'Must equal the node count in board_graph.json (85). Reseed rather than editing this.',
  },
  {
    key: 'cancerGraceTurns',
    label: 'Cancer grace turns',
    hint: "Turns a player has to come back and cure a cancer before it spreads.",
  },
  {
    key: 'vesselEncounterPercent',
    label: 'Vessel encounter rate (%)',
    hint:
      'How often landing on a red vein/artery space raises an encounter. 100 is the rulebook read literally; lower it to make players travel further per turn. Pacing knob — playtest before changing.',
  },
  { key: 'startingProcedureCards', label: 'Starting procedure cards' },
  { key: 'startingSurgeryCards', label: 'Starting surgery cards' },
  { key: 'startingMedicineCards', label: 'Starting medicine cards' },
  { key: 'startingPathologyCards', label: 'Starting pathology cards' },
  { key: 'startingPreventativeCards', label: 'Starting preventative cards' },
];

export function ConfigPage() {
  const config = useResource(() => api<{ config: GameConfig }>('/v1/admin/config'));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config.data) return;

    const { id: _id, version: _version, updatedAt: _updatedAt, ...rest } = config.data.config;
    setDraft(rest);
  }, [config.data]);

  if (config.loading && !draft) return <Spinner />;
  if (config.error) return <Status error={config.error} />;
  if (!draft || !config.data) return null;

  const unlimitedExchanges = draft.exchangesPerTurn === null;

  async function save() {
    if (!draft) return;

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const result = await api<{ config: GameConfig; changes: Changes }>('/v1/admin/config', {
        method: 'PATCH',
        body: draft,
      });

      setMessage(summariseChanges(result.changes));
      await config.reload();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel
      title="Game config"
      actions={
        <>
          <span className="muted">content version {config.data.config.version}</span>
          <button className="button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <p className="muted">
        Every value the spec marks <code>[OPEN]</code> lives here rather than in the app, so
        answering the client is a row change, not a release. Saving bumps the content version and
        the app picks it up on its next fetch.
      </p>

      <div className="grid">
        {NUMBER_FIELDS.map((field) => (
          <Field key={String(field.key)} label={field.label} hint={field.hint}>
            <NumberInput
              value={draft[field.key] as number}
              onChange={(value) => setDraft({ ...draft, [field.key]: value === '' ? 0 : value })}
            />
          </Field>
        ))}

        <Field label="Exchanges per turn" hint="Unlimited is a real setting, not 'unset'">
          <div className="row">
            <NumberInput
              value={draft.exchangesPerTurn ?? ''}
              onChange={(value) => setDraft({ ...draft, exchangesPerTurn: value === '' ? null : value })}
            />
            <Toggle
              checked={unlimitedExchanges}
              label="Unlimited"
              onChange={(checked) =>
                setDraft({ ...draft, exchangesPerTurn: checked ? null : 1 })
              }
            />
          </div>
        </Field>
      </div>

      <Status error={error} message={message} />
    </Panel>
  );
}
